import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style.css';

function ResultModel({ filters, keyword }) {
    const navigate = useNavigate();
    const [artifacts, setArtifacts] = useState([]);
    const [filteredArtifacts, setFilteredArtifacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState(null);

    // Fetch data from API
    useEffect(() => {
        const fetchArtifacts = async () => {
            try {
                setLoading(true); // Start loading
                const response = await fetch('https://geoid-rest.vercel.app/models');
                const data = await response.json();
                const publicModel = data.filter(model => model.access === 'public');
                setArtifacts(publicModel);
                setFilteredArtifacts(publicModel);
            } catch (error) {
                console.error('Error fetching artifacts:', error);
            } finally {
                setLoading(false); // Stop loading
            }
        };

        fetchArtifacts();
    }, []);

    useEffect(() => {
        let filtered = artifacts;

        // Apply filters
        if (filters.country) {
            filtered = filtered.filter(artifact => artifact.data.country?.toLowerCase() === filters.country.toLowerCase());
        }
        if (filters.state) {
            filtered = filtered.filter(artifact => artifact.data.state?.toLowerCase() === filters.state.toLowerCase());
        }
        if (filters.category) {
            filtered = filtered.filter(artifact => artifact.data.type?.toLowerCase() === filters.category.toLowerCase());
        }
        if (filters.majorLithology) {
            filtered = filtered.filter(artifact => artifact.data.majorLithology?.toLowerCase() === filters.majorLithology.toLowerCase());
        }
        if (filters.discipline) {
            filtered = filtered.filter(artifact => artifact.data.discipline?.toLowerCase() === filters.discipline.toLowerCase());
        }
        if (filters.rockType) {
            filtered = filtered.filter(artifact => artifact.data.rockType?.toLowerCase() === filters.rockType.toLowerCase());
        }
        if (filters.formation) {
            filtered = filtered.filter(artifact => artifact.data.formation?.toLowerCase() === filters.formation.toLowerCase());
        }
        if (filters.age) {
            filtered = filtered.filter(artifact => artifact.data.age?.toLowerCase() === filters.age.toLowerCase());
        }

        // Apply keyword filter
        if (keyword) {
            filtered = filtered.filter(artifact => 
                artifact.name?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.location?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.title?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.author?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.country?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.state?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.district?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.city?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.type?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.discipline?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.formation?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.rockType?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.majorLithology?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.age?.toLowerCase().includes(keyword.toLowerCase()) ||
                artifact.data?.keyword?.toLowerCase().includes(keyword.toLowerCase())
            );
        }

        setFilteredArtifacts(filtered);
    }, [filters, keyword, artifacts]);

    // Function to sort artifacts
    const sortArtifacts = (artifacts) => {
        if (!sortOrder) return artifacts; // Return unsorted artifacts if sortOrder is null
        return [...artifacts].sort((a, b) => {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            if (sortOrder === 'asc') {
                return nameA.localeCompare(nameB);
            } else {
                return nameB.localeCompare(nameA);
            }
        });
    };

    // Apply sorting to filtered artifacts
    const sortedArtifacts = sortArtifacts(filteredArtifacts);

    const handleSortToggle = () => {
        setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };

    const resultCount = sortedArtifacts.length;

    const handleViewModel = (model, data, artifact) => {
        navigate('/model/view-model', { state: { model, data, artifact } });
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <p>Loading data...</p>
            </div>
        );
    }

    return (
        <div className="artifact-result-page">
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                <p style={{ textAlign: 'left' }}>Models: {resultCount} result{resultCount !== 1 ? 's' : ''} found</p>
                <button className='sortBy' onClick={handleSortToggle}>
                    Sort By Alphabet {
                    sortOrder === 'asc' ? 
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-down" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1"/>
                    </svg> : 
                    
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-up" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5"/>
                    </svg>
                    }
                </button>
            </div>
            <div className="artifact-result-grid">
                {sortedArtifacts.map((artifact, index) => (
                    <div className="artifact-result-card" key={index}>
                        <img src={artifact.imgSrc} alt={artifact.name} className="artifact-result-image" />
                        <div className="artifact-result-info">
                            <h3>
                                {artifact.name.length > 27 
                                    ? `${artifact.name.slice(0, 27)}...` 
                                    : artifact.name}
                            </h3>
                            <p>{artifact.location}</p>
                            <button className="artifact-result-view-button" onClick={() => handleViewModel(artifact.model, artifact.data, artifact)}>
                                View 3D Model >
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ResultModel;